import {
  AutocompletePredictionPredictionCategory,
  AutocompletePredictionRestrictionTypes,
  AutocompletePredictionTypes,
  QueryResultType,
} from '../types';
import { autocompletePredictionToSearchResults } from 'utils/google-maps/googleSearchPredictions';
import { CountryCode, CountryCodeList } from 'types/countries';
import SearchPrediction from 'data/search-predictions';
import { getPredictionCategory } from '../predictionClassifier';
import GoogleMapsApiLoader from './loader';

const PLACE_TYPES_TO_REQUEST: string[] = [
  AutocompletePredictionRestrictionTypes.CITIES,
  AutocompletePredictionRestrictionTypes.REGIONS,
  AutocompletePredictionRestrictionTypes.GEOCODE,
  AutocompletePredictionRestrictionTypes.ADDRESS,
];

const SUPPORTED_PLACE_TYPES: string[] = [
  AutocompletePredictionTypes.NEIGHBORHOOD,
  AutocompletePredictionTypes.GEOCODE,
  AutocompletePredictionTypes.LOCALITY,
  AutocompletePredictionTypes.SUBLOCALITY,
  AutocompletePredictionTypes.SUBLOCALITY_LEVEL_1,
  AutocompletePredictionTypes.ADMINISTRATIVE_AREA_LEVEL_1,
  AutocompletePredictionTypes.ADMINISTRATIVE_AREA_LEVEL_2,
  AutocompletePredictionTypes.ADMINISTRATIVE_AREA_LEVEL_3,
  AutocompletePredictionTypes.ROUTE,
  AutocompletePredictionTypes.POINT_OF_INTEREST,
];

const PLACE_TYPE_INDEX = 0;

export async function getAutoCompleteService() {
  try {
    const { AutocompleteService } = await GoogleMapsApiLoader.importLibrary('places');
    return new AutocompleteService();
  } catch (_error) {
    return undefined;
  }
}

// Create a new session token.
let sessionToken: google.maps.places.AutocompleteSessionToken | null = null;

export async function createAutocompletionRequest(
  input: string,
  type: string = AutocompletePredictionRestrictionTypes.GEOCODE,
  country: readonly CountryCode[]
) {
  const { AutocompleteSessionToken } = await GoogleMapsApiLoader.importLibrary('places');

  if (!sessionToken) {
    sessionToken = new AutocompleteSessionToken();
  }
  const request: google.maps.places.AutocompletionRequest = {
    input,
    componentRestrictions: { country: [... country]},
    types: [type],
    sessionToken,
  };
  return request;
}

export const getHomeAppraisalPredictions = async (
  input: string,
  country: CountryCode = CountryCodeList.CANADA, // Country of origin
  apiKey: string = process.env.NEXT_PUBLIC_GOOGLE_API_KEY || ''
): Promise<SearchPrediction[]> => {
  if (!(apiKey?.length > 0)) {
    throw new Error('No Google API key was found.');
  }

  const autocompleteService = await getAutoCompleteService();
  // Only get this type to avoid getting too broad results like cities
  const types = ['street_address', 'street_number', 'route'];
  const allPredictions = [];

  if (autocompleteService) {
    for (const type of types) {
      // Only pass one country per request
      const request = await createAutocompletionRequest(input, type, [country]);
      const response = await autocompleteService.getPlacePredictions(request);
      allPredictions.push(...response.predictions);
    }

    const uniquePredictions = Array.from(new Map(allPredictions.map(prediction => [prediction.place_id, prediction])).values());

    // Show the country of origin first
    const countryName = country === CountryCodeList.CANADA ? 'Canada' : 'USA';
    const sortedPredictions = uniquePredictions.sort((a, b) => {
      const isACountryOfOrigin = a.terms.some(term => term.value === countryName);
      const isBCountryOfOrigin = b.terms.some(term => term.value === countryName);
      return isACountryOfOrigin === isBCountryOfOrigin ? 0 : isACountryOfOrigin ? -1 : 1;
    });

    // Parse results to make it works with advanced search dropdown component
    const parsedPredictions = autocompletePredictionToSearchResults(sortedPredictions) as SearchPrediction[];

    return parsedPredictions;
  } else return [];
};

export default async function query(
  input: string,
  country: readonly CountryCode[],
  apiKey: string = process.env.NEXT_PUBLIC_GOOGLE_API_KEY || ''
) {
  if (!(apiKey?.length > 0)) {
    throw new Error('No Google API key was found.');
  }

  const autocompleteService = await getAutoCompleteService();

  if (autocompleteService) {
    const cityPredictions: google.maps.places.AutocompletePrediction[] = [];
    const neighborhoodPredictions: google.maps.places.AutocompletePrediction[] = [];
    const streetPredictions: google.maps.places.AutocompletePrediction[] = [];
    const provincePredictions: google.maps.places.AutocompletePrediction[] = [];
    const postalCodePredictions: google.maps.places.AutocompletePrediction[] = [];
    const uniquePlaceId = new Set<string>();

    for (let index = 0; index < PLACE_TYPES_TO_REQUEST.length; index++) {
      const type = PLACE_TYPES_TO_REQUEST[index];
      const request = await createAutocompletionRequest(input, type, country);
      const response = await autocompleteService.getPlacePredictions(request);

      if (response?.predictions) {
        response?.predictions.reduce(function (
          uniquePlaceId: Set<string>,
          prediction: google.maps.places.AutocompletePrediction
        ) {
          if (
            SUPPORTED_PLACE_TYPES.includes(
              prediction.types[PLACE_TYPE_INDEX]
            ) &&
            !uniquePlaceId.has(prediction.place_id)
          ) {
            const predictionCategory = getPredictionCategory(prediction.types);
            switch (predictionCategory) {
              case AutocompletePredictionPredictionCategory.CITY:
                cityPredictions.push(prediction);
                break;
              case AutocompletePredictionPredictionCategory.NEIGHBORHOOD:
                neighborhoodPredictions.push(prediction);
                break;
              case AutocompletePredictionPredictionCategory.STREET:
                streetPredictions.push(prediction);
                break;
              case AutocompletePredictionPredictionCategory.PROVINCE:
                provincePredictions.push(prediction);
                break;
              case AutocompletePredictionPredictionCategory.POSTAL_CODE:
                postalCodePredictions.push(prediction);
                break;
              default:
              // Ignore 'other' predictions for now
                break;
            }
            uniquePlaceId.add(prediction.place_id);
          }
          return uniquePlaceId;
        },
        uniquePlaceId);
      }
    }

    const result: QueryResultType = {
      city: cityPredictions,
      neighborhood: neighborhoodPredictions,
      street: streetPredictions,
      // right now expCommercial uses province & postal code predictions from google maps
      province: provincePredictions,
      postalCode: postalCodePredictions,
    };

    return result;
  }

  throw new Error('AutocompleteService could not be created.');
}